import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInterfacePreference } from '../../hooks/useInterfacePreference';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { MAIN_NAV_ITEMS, MORE_NAV_ITEMS } from '../../config/navItems';
import VolumeControl from '../VolumeControl/VolumeControl';
import './ProHeader.css';

function ProHeader({ isConnected, isSubscribed, onToggleChart, chartVisible, onOpenFilter }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();
  const { setPreference } = useInterfacePreference();
  const showAiTrader = useFeatureFlag('show-ai-trader-nav', true);
  const isLive = isConnected && isSubscribed;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mWhalesOpen, setMWhalesOpen] = useState(false);
  const [mMoreOpen, setMMoreOpen] = useState(false);
  const headerRef = useRef(null);

  // Only AI Trader is feature-gated today (show-ai-trader-nav); preserve default-true.
  const isNavItemVisible = (item) => !item.featureFlag || showAiTrader;

  const handleSwitchToBasic = () => {
    setPreference('basic');
    navigate(localizedPath('/trades'));
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Collapse the menu's sub-sections whenever the menu closes (any path).
  useEffect(() => {
    if (!isMobileMenuOpen) { setMWhalesOpen(false); setMMoreOpen(false); }
  }, [isMobileMenuOpen]);

  // Close the mobile menu when clicking outside the header.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const onClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="pro-header" ref={headerRef}>
      <div className="pro-header-logo">
        <span className="pro-logo-bracket">[</span>
        <span className="pro-logo-text">TRADERMAP.IO</span>
        <span className="pro-logo-pro">PRO</span>
        <span className="pro-logo-bracket">]</span>
      </div>

      <nav className="pro-header-nav">
        {/* Whale Trades with dropdown (Basic/Pro mode switcher — terminal-only control) */}
        <div className="pro-nav-dropdown">
          <button className="pro-nav-item pro-nav-item--active">
            WHALE TRADES
          </button>
          <div className="pro-nav-dropdown-menu">
            <button className="pro-nav-dropdown-item" onClick={handleSwitchToBasic}>
              Basic <span className="pro-nav-dropdown-badge pro-nav-dropdown-badge--standard">STANDARD</span>
            </button>
            <button className="pro-nav-dropdown-item pro-nav-dropdown-item--selected">
              Professional <span className="pro-nav-dropdown-badge pro-nav-dropdown-badge--terminal">TERMINAL</span>
            </button>
          </div>
        </div>

        {/* Main nav from the shared config. Skip 'trades' — the mode switcher
            above owns that slot on the terminal bar. */}
        {MAIN_NAV_ITEMS.filter((i) => i.key !== 'trades').filter(isNavItemVisible).map((item) => (
          <Link key={item.key} className="pro-nav-item" to={localizedPath(item.path)}>
            {t(item.i18nKey, item.fallback)}
            {item.badge && <span className="pro-nav-badge pro-nav-badge--new">{item.badge}</span>}
          </Link>
        ))}

        {/* More dropdown from the shared config */}
        <div className="pro-more-dropdown">
          <button className="pro-nav-item">
            MORE <span className="pro-nav-arrow">&#9662;</span>
          </button>
          <div className="pro-more-dropdown-menu">
            {MORE_NAV_ITEMS.filter(isNavItemVisible).map((item) => (
              <Link key={item.key} className="pro-more-link" to={localizedPath(item.path)}>
                {t(item.i18nKey, item.fallback)}
                {item.badge && <span className="pro-nav-badge pro-nav-badge--new">{item.badge}</span>}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="pro-header-right">
        {/* Chart toggle */}
        <button
          className={`pro-header-btn ${chartVisible ? 'pro-header-btn--active' : ''}`}
          onClick={onToggleChart}
        >
          &#9636; CHART
        </button>

        {/* Filter button */}
        <button className="pro-header-btn" onClick={onOpenFilter}>
          &#9636; FILTER
        </button>

        {/* Volume control */}
        <VolumeControl />

        {/* Live indicator */}
        <div className={`pro-live-indicator ${isLive ? '' : 'pro-live-indicator--inactive'}`}>
          <span className="pro-live-dot" />
          LIVE
        </div>

        {/* Mobile hamburger (mobile only) */}
        <button
          className={`pro-header-toggler ${isMobileMenuOpen ? 'pro-header-toggler--open' : ''}`}
          onClick={() => setIsMobileMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          &#9776;
        </button>
      </div>

      {/* Mobile menu — mirrors the desktop terminal bar: WHALE TRADES switcher,
          main links, then a MORE group. The hamburger entry point (terminal
          had no mobile menu before). */}
      <div className={`pro-mobile-menu ${isMobileMenuOpen ? 'show' : ''}`}>
        {/* WHALE TRADES — Basic/Pro mode switcher (expandable) */}
        <button
          className={`pro-mobile-link pro-mobile-group ${mWhalesOpen ? 'pro-mobile-group--open' : ''}`}
          onClick={() => setMWhalesOpen((v) => !v)}
          aria-expanded={mWhalesOpen}
        >
          WHALE TRADES
          <span className="pro-mobile-caret">&#9662;</span>
        </button>
        {mWhalesOpen && (
          <div className="pro-mobile-sub">
            <button
              className="pro-mobile-sublink"
              onClick={() => { closeMobileMenu(); handleSwitchToBasic(); }}
            >
              Basic <span className="pro-nav-dropdown-badge pro-nav-dropdown-badge--standard">STANDARD</span>
            </button>
            <button className="pro-mobile-sublink pro-mobile-sublink--selected" disabled>
              Professional <span className="pro-nav-dropdown-badge pro-nav-dropdown-badge--terminal">TERMINAL</span>
            </button>
          </div>
        )}

        {/* Main links (trades is the WHALE TRADES switcher above) */}
        {MAIN_NAV_ITEMS.filter((i) => i.key !== 'trades').filter(isNavItemVisible).map((item) => (
          <Link
            key={item.key}
            className="pro-mobile-link"
            to={localizedPath(item.path)}
            onClick={closeMobileMenu}
          >
            {t(item.i18nKey, item.fallback)}
            {item.badge && <span className="pro-nav-badge pro-nav-badge--new">{item.badge}</span>}
          </Link>
        ))}

        {/* MORE — secondary links (expandable) */}
        <button
          className={`pro-mobile-link pro-mobile-group ${mMoreOpen ? 'pro-mobile-group--open' : ''}`}
          onClick={() => setMMoreOpen((v) => !v)}
          aria-expanded={mMoreOpen}
        >
          {t('nav.more', 'More')}
          <span className="pro-mobile-caret">&#9662;</span>
        </button>
        {mMoreOpen && (
          <div className="pro-mobile-sub">
            {MORE_NAV_ITEMS.filter(isNavItemVisible).map((item) => (
              <Link
                key={item.key}
                className="pro-mobile-sublink"
                to={localizedPath(item.path)}
                onClick={closeMobileMenu}
              >
                {t(item.i18nKey, item.fallback)}
                {item.badge && <span className="pro-nav-badge pro-nav-badge--new">{item.badge}</span>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}

export default ProHeader;
